import { itemService } from "@/api/item.service";
import { iconService } from "@/api/icon.service";
import { itemDataInRootState } from "@/helpers/store-helpers";
import router from '@/router/index';

export const state = {
  selectedItemId: null,
  category: null
}

export const actions = {
  resetItem({ commit }) {
    commit("setSelectedItem", null);
  },
  switchItem({ commit, rootState }, { id, callback }) {
    commit("setSelectedItem", id);
    const { property, area, item } = itemDataInRootState(rootState);
    commit(
      'navigation/breadcrumbAdded',
      {
        text: `${item.category} > ${item.sub_category} > ${item.name}`,
        level: 3,
        itemId: id,
        to: `/properties/${property.id}/areas/${area.id}/items/${id}`,
        active: true
      },
      { root: true }
    );
    if (callback) callback();
  },
  uploadPhoto({ commit, dispatch, rootState }, { file, callback }) {
    const { property, area, item } = itemDataInRootState(rootState);
    itemService.uploadFile(property.id, area.id, item.id, 'photos', file)
      .then(response => {
        commit(
          "property/setItemPhotos",
          {
            propertyId: property.id,
            areaId: area.id,
            itemId: item.id,
            photos: response.photos
          },
          { root: true }
        );
        if (callback) callback();
        dispatch('property/cacheProperties', null, { root: true });
      });
  },
  deletePhoto({ commit, dispatch, rootState }, { id, callback }){
    const { property, area, item } = itemDataInRootState(rootState);
    itemService.deleteFile(property.id, area.id, item.id, 'photos', id)
      .then(response => {
        commit(
          "property/setItemPhotos",
          {
            propertyId: property.id,
            areaId: area.id,
            itemId: item.id,
            photos: response.photos
          },
          { root: true }
        );
        if (callback) callback();
        dispatch('property/cacheProperties', null, { root: true });
      });
  },
  uploadDocument({ commit, dispatch, rootState }, { file, callback }) {
    const { property, area, item } = itemDataInRootState(rootState);
    itemService.uploadFile(property.id, area.id, item.id, 'documents', file)
      .then(response => {
        commit(
          "property/setItemDocuments",
          {
            propertyId: property.id,
            areaId: area.id,
            itemId: item.id,
            documents: response.documents
          },
          { root: true }
        );
        if (callback) callback();
        dispatch('property/cacheProperties', null, { root: true });
      });
  },
  updateDocument({ commit, dispatch, rootState }, {payload, callback }) {
        const formattedPayload = {
          documents_attributes: [{
            id: payload.documentId,
            name: payload.name,
            date_of_document: payload.date
          }]
              };
        const { property, area, item } = itemDataInRootState(rootState);
    itemService.updateItem(property.id, area.id, item.id, formattedPayload)
      .then(response => {
        commit(
          "property/setItemDocuments",
          {
            propertyId: property.id,
            areaId: area.id,
            itemId: item.id,
            documents: response.documents
          },
          { root: true }
        );
        if (callback) callback();
        dispatch("notification/success", 'Document updated.', { root: true });

        dispatch('property/cacheProperties', null, { root: true });
      });
  },
  deleteDocument({ commit, dispatch, rootState }, { id, callback }){
    const { property, area, item } = itemDataInRootState(rootState);
    itemService.deleteFile(property.id, area.id, item.id, 'documents', id)
      .then(response => {
        commit(
          "property/setItemDocuments",
          {
            propertyId: property.id,
            areaId: area.id,
            itemId: item.id,
            documents: response.documents
          },
          { root: true }
        );
        if (callback) callback();
        dispatch('property/cacheProperties', null, { root: true });
      });
  },
  createItem({ commit, rootState, dispatch }, payload) {
    const { property, area } = itemDataInRootState(rootState);
    itemService.createItem(property.id, area.id, payload)
      .then(data => {
        commit("property/setItem", { propertyId: property.id, areaId: area.id, data }, { root: true });
        router.push({ name: 'Item', params: { propId: property.id, areaId: area.id, itemId: data.id, keepNotifications: true } });
        dispatch("notification/success", 'Item created.', { root: true });
        dispatch("property/cacheProperties", null, { root: true });
      });
  },
  updateItem({ commit, rootState, dispatch }, payload) {
    const { property, area, item } = itemDataInRootState(rootState);
    itemService.updateItem(property.id, area.id, item.id, payload)
      .then(data => {
        commit(
          "property/setItem",
          { propertyId: property.id, areaId: area.id, itemId: data.id, data },
          { root: true }
        );
        commit(
          "navigation/updateBreadcrumbLabel",
          { label: `${data.category} > ${data.sub_category} > ${data.name}`, itemId: data.id },
          { root: true }
        );
        dispatch("notification/success", 'Item updated.', { root: true });
        dispatch("property/cacheProperties", null, { root: true });
      });
  },
  setCategory({ commit }, { category }) {
    let categoryPath = '/items', // All items
          icon = null;

    if (category) {
      categoryPath = category ? `/items/${category}` : "/items",
      icon = iconService.getIconByCategory(category);
    }

    commit(
      'navigation/breadcrumbAdded',
      {
        text: icon ? icon.name : 'All',
        level: icon ? 2 : 1, // Root of breadcrumb when no category provided
        to: categoryPath,
        active: true
      },
      { root: true }
    );
    commit('setCategory', { category });
  },
  deleteItem({ commit, dispatch }, { id, propertyId, areaId }){
    itemService.deleteItem(propertyId, areaId, id)
      .then(() => {
        commit("property/removeItem", { propertyId: propertyId, areaId: areaId, itemId: id }, { root: true });
        dispatch("notification/success", 'Item deleted.', { root: true });
        dispatch("property/cacheProperties", null, { root: true });
      });
  },
  updateItemArea({ commit, dispatch, rootState }, payload) {
    const { property, area, item } = itemDataInRootState(rootState);
    itemService.updateItem(property.id, area.id, item.id, payload)
      .then(data => {
        commit("property/removeItem", { propertyId: property.id, areaId: area.id, itemId: item.id }, { root: true });
        commit(
          "property/setItem",
          { propertyId: property.id, areaId: data.area_id, data },
          { root: true }
        );
        router.push({ name: 'Item', params: { propId: property.id, areaId: data.area_id, itemId: data.id, keepNotifications: true } });
        dispatch("notification/success", 'Item updated.', { root: true });
        dispatch("property/cacheProperties", null, { root: true });
      });
  }
};

export const mutations = {
  setSelectedItem(state, itemId) {
    state.selectedItemId = itemId;
  },
  setCategory(state, { category }) {
    state.category = category;
  },
};

export const getters = {
  selectedItem(_state, _getters, rootState) {
    const { item } = itemDataInRootState(rootState);

    return item;
  },
  inventoryItemsByCategory(state, _getters, rootState) {
    let itemList = {};

    const selectedPropertyId = rootState.property.selectedPropertyId,
          selectedAreaId = rootState.area.selectedAreaId,
          property = rootState.property.properties.find(property => property.id == selectedPropertyId);

    if (property) {
      if (selectedAreaId) {
        const area = property.areas.find(area => area.id == selectedAreaId);
        area.items.forEach(item => {
          if (item.category) {
            const slug = iconService.getCategorySlug(item.category);

            if (itemList[slug] == undefined || itemList[slug] == null)
              itemList[slug] = [];

            item.propertyName = property.name;
            itemList[slug].push(item);
          }
        });
      } else {
        property.areas.forEach(area => {

          area.items.forEach(item => {
            if (item.category) {
              const slug = iconService.getCategorySlug(item.category);

              if (itemList[slug] == undefined || itemList[slug] == null)
                itemList[slug] = [];

              item.propertyName = property.name;
              itemList[slug].push(item);
            }
          });
        });
      }
    }

    if (state.category && itemList[state.category]) {
      return itemList[state.category];
    }

    return {};
  },
  inventoryItems(state, _getters, rootState) {
    let itemList = {};

    rootState.property.properties.forEach(property => {
      property.areas.forEach(area => {
        area.items.forEach(item => {
          if (item.category) {
            const slug = iconService.getCategorySlug(item.category);

            if (itemList[slug] == undefined || itemList[slug] == null)
              itemList[slug] = [];

            item.propertyName = area.name;
            itemList[slug].push(item);
          }
        });
      });
    });

    return itemList;
  }
};
